import { TextAnimate } from '@neo/extras/text-animate'
import { Button } from '@neo/primitives/button'
import { Tooltip } from '@neo/primitives/tooltip'
import InlineEdit from '@neo/shared/components/inline-edit'
import { Check, Loader2, Pencil } from 'lucide-react'
import { useRef, useState } from 'react'
import { useRenameFlow } from '../../hooks/use-rename-flow'
import { useFlowStore } from '../../store'
import type { FlowData } from '../../types'
import { FLOW_NAME_MAX_LENGTH, resolveRenameAction } from '../../utils/rename'

interface FlowTitleEditorProps {
  flowData?: FlowData
  isDefault?: boolean
  // Route-level "new flow" signal. Needed to tell a genuinely new (unsaved) flow apart from
  // an existing flow whose `flowData` hasn't loaded yet — both lack a `phlo.id`.
  isNewFlow: boolean
}

const TITLE_CLASSES =
  'focus-visible:ring-transparent border-0 shadow-none text-h3-500 text-secondary -ml-1 whitespace-nowrap overflow-hidden text-ellipsis focus:overflow-x-auto focus:[text-overflow:clip] hide-scrollbar block min-w-24 max-w-full'

export const FlowTitleEditor: React.FC<FlowTitleEditorProps> = ({
  flowData,
  isDefault,
  isNewFlow,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  // Bumped to force-remount InlineEdit when reverting the contenteditable to currentName.
  const [resetNonce, setResetNonce] = useState(0)
  const [validationError, setValidationError] = useState<string | null>(null)

  // Only a flow that exists in the DB has a phlo.id. An unsaved flow keeps its name in the
  // store until the first save/publish — renaming it must never hit the API. flowId is only
  // read by the mutation, which fires solely on the persisted ('save') path.
  const flowId = flowData?.phlo?.id ?? ''
  const isPersisted = Boolean(flowData?.phlo?.id)
  // A new flow commits to the store; a persisted flow commits to the API. An existing flow
  // whose data is still loading is neither yet — block the commit so it can't be misrouted to
  // the store-only path.
  const canCommitRename = isNewFlow || isPersisted

  const storeFlowName = useFlowStore((state) => state.currentFlowName)
  const shouldStreamFlowNameEffect = useFlowStore((state) => state.shouldStreamFlowNameEffect)
  const setCurrentName = useFlowStore((state) => state.setCurrentFlowName)
  const setFlowNameUserEdited = useFlowStore((state) => state.setFlowNameUserEdited)
  const setShouldStreamFlowNameEffect = useFlowStore((state) => state.setShouldStreamFlowNameEffect)

  // Use store name if available, otherwise fall back to flow data
  const currentName = storeFlowName || flowData?.phlo?.name || ''

  const { rename, status, errorMessage, reset } = useRenameFlow(flowId, { updateStoreName: true })
  const displayError = validationError ?? errorMessage

  const revertEditor = () => setResetNonce((nonce) => nonce + 1)

  // Clear any standing error as the user edits; the actual rename is committed on blur/Enter.
  const handleChange = () => {
    if (validationError) setValidationError(null)
    if (errorMessage) reset()
  }

  const commitName = (value: string) => {
    if (!canCommitRename) {
      revertEditor()
      return
    }
    const action = resolveRenameAction({ value, currentName, isPersisted })
    switch (action.type) {
      case 'invalid':
        reset() // drop any lingering "saved" indicator so it can't co-render with the error
        setValidationError(action.error)
        revertEditor()
        break
      case 'store':
        // Unsaved flow: hold the name in the store until the first save/publish.
        setValidationError(null)
        setFlowNameUserEdited(true)
        setCurrentName(action.name)
        break
      case 'save':
        setValidationError(null)
        setFlowNameUserEdited(true)
        rename(action.name)
        break
      case 'noop':
        setValidationError(null)
        revertEditor()
        break
    }
  }

  /** Focuses the contenteditable element and selects all its text */
  const handlePencilClick = () => {
    // querySelector scoped to our wrapper — finds the InlineEdit's contenteditable div
    const editableEl = wrapperRef.current?.querySelector<HTMLElement>('[contenteditable]')
    if (!editableEl) return

    editableEl.focus()

    // Select all text inside the contenteditable using the Selection API
    const range = document.createRange()
    range.selectNodeContents(editableEl)
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
  }

  const renderTitle = () => {
    if (isDefault) {
      return (
        <Tooltip content="Default agent name cannot be edited">
          <span
            tabIndex={0}
            role="note"
            className="text-h3-500 text-secondary -ml-1 whitespace-nowrap overflow-hidden text-ellipsis block min-w-24 max-w-full cursor-default"
            data-testid="input-flow-title"
          >
            {currentName}
          </span>
        </Tooltip>
      )
    }

    if (shouldStreamFlowNameEffect) {
      return (
        <TextAnimate
          by="character"
          animation="blurInUp"
          duration={1}
          startOnView={false}
          className="text-h3-500 text-secondary -ml-1 whitespace-nowrap overflow-hidden text-ellipsis block min-w-24 max-w-full"
          key={currentName}
          onAnimationComplete={() => setShouldStreamFlowNameEffect(false)}
          once
        >
          {currentName}
        </TextAnimate>
      )
    }

    return (
      <InlineEdit
        key={resetNonce}
        focusOnMount={false}
        name={currentName}
        initialHtml={currentName}
        onChange={handleChange}
        onEnter={(_, value) => commitName(value)}
        onBlur={(_, value) => commitName(value)}
        maxLength={FLOW_NAME_MAX_LENGTH}
        className={`${TITLE_CLASSES}${displayError ? ' ring-1 ring-destructive rounded-sm' : ''}`}
        data-testid="input-flow-title"
      />
    )
  }

  return (
    <div
      ref={wrapperRef}
      className="relative min-w-0 overflow-visible pl-1 flex items-center pr-1 group/title"
    >
      {renderTitle()}
      {!isDefault && !shouldStreamFlowNameEffect && (
        <Tooltip content="Edit agent name">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Edit agent name"
            className="h-6 w-6 shrink-0 p-0 ml-1 opacity-0 transition-opacity group-hover/title:opacity-100 focus-visible:opacity-100"
            onClick={handlePencilClick}
            data-testid="button-edit-flow-title"
          >
            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </Tooltip>
      )}
      {!isDefault && !displayError && (status === 'saving' || status === 'saved') && (
        <span className="ml-1 flex shrink-0 items-center" aria-live="polite" aria-atomic="true">
          {status === 'saving' ? (
            <>
              <Loader2
                aria-hidden="true"
                className="h-4 w-4 animate-spin text-muted-foreground"
                data-testid="rename-status-saving"
              />
              <span className="sr-only">Saving agent name</span>
            </>
          ) : (
            <>
              <Check
                aria-hidden="true"
                className="h-4 w-4 text-status-success"
                data-testid="rename-status-saved"
              />
              <span className="sr-only">Agent name saved</span>
            </>
          )}
        </span>
      )}
      {displayError && (
        <span
          role="alert"
          data-testid="rename-error"
          className="absolute left-1 top-full mt-0.5 text-s-400 text-destructive whitespace-nowrap"
        >
          {displayError}
        </span>
      )}
    </div>
  )
}
